<template>
  <div class="h-full group">
      <component 
      :is="cardLinkComponent"
      :href="cardHref"
      :to="cardTo"
      class="flex flex-col h-full overflow-hidden cursor-pointer no-underline"
      :data-search-card="isColivingSearchResult ? 'true' : undefined"
      @click="handleCardClick"
    >
    <!-- NIVEL 2 - C: Image container con hover carousel -->
      <div 
        class="block w-full relative overflow-hidden bg-gray-200 rounded-xl transition-all duration-300"
        :class="[
          isMobile && isColivingSearchResult ? 'aspect-[2/1]' :
          isMobile ? 'aspect-[4/3]' :
          isColivingSearchResult ? 'aspect-[4/3]' : 'aspect-[16/10]'
        ]"
        @mouseenter="startImageCarousel"
        @mouseleave="stopImageCarousel"
        @touchstart.passive="handleTouchStart"
        @touchend.passive="handleTouchEnd"
      >
        <img
          v-if="displayImageUrl"
          :key="`${contract.id}-${currentImageIndex}-${listingImageErrorCount}`"
          :src="displayImageUrl"
          :alt="optimizedTitle || t('contractCard.noImage')"
          class="absolute inset-0 w-full h-full object-cover"
          :fetchpriority="shouldPrioritizeImage ? 'high' : undefined"
          :loading="shouldPrioritizeImage ? 'eager' : 'lazy'"
          decoding="async"
          referrerpolicy="no-referrer"
          :width="480"
          :height="isMobile && isColivingSearchResult ? 240 : 360"
          @error="handleListingImageError"
        />
        <!-- Fallback content cuando no hay imagen -->
        <div v-if="!firstImageUrl" class="absolute inset-0 flex items-center justify-center bg-gray-200">
          <div class="text-center text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p class="text-xs">{{ t('contractCard.noImage') }}</p>
          </div>
        </div>
        
        <!-- Indicadores de imágenes (dots) — tapeables en mobile -->
        <div v-if="false && imageCount > 1" class="absolute left-1/2 -translate-x-1/2 flex gap-1 z-10" :class="shouldShowPrice ? 'bottom-9' : 'bottom-3'">
          <button
            v-for="(_, idx) in Math.min(imageCount, 5)"
            :key="idx"
            type="button"
            @click.prevent.stop="goToImage(idx)"
            class="p-1.5 focus:outline-none"
            :aria-label="`Imagen ${idx + 1}`"
          >
            <div
              class="h-1.5 rounded-full transition-all duration-200"
              :class="currentImageIndex === idx ? 'bg-white w-4' : 'bg-white/60 w-1.5'"
            ></div>
          </button>
        </div>
        
        <!-- Badges superior izquierda -->
        <div class="absolute top-3 left-3 flex flex-col gap-2 z-10">
          <!-- Badges de urgencia (disponibilidad limitada / nuevo) -->
          <div
            v-if="isColivingSearchResult && lowAvailability"
            class="inline-flex items-center pl-2 pr-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm shadow-sm ring-1 ring-black/5"
          >
            <span class="text-[11px] font-medium text-rose-600 tracking-tight">
              {{ t('contractCard.urgency.limitedAvailability', contract.availableRooms) }}
            </span>
          </div>
          <div
            v-else-if="isColivingSearchResult && isNewListing"
            class="inline-flex items-center pl-2 pr-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm shadow-sm ring-1 ring-black/5"
          >
            <span class="text-[11px] font-medium text-emerald-600 tracking-tight">{{ t('contractCard.urgency.isNew') }}</span>
          </div>

          <!-- Badge de valor (precio competitivo) -->
          <div
            v-if="showValueBadge"
            class="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm shadow-sm ring-1 ring-black/5"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-emerald-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            <span class="text-[11px] font-medium text-gray-800 tracking-tight">{{ t('contractCard.goodPrice') }}</span>
          </div>
          
          <!-- Badge destacado para agentes PRO -->
          <div
            v-if="contract.isProAgent"
            class="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm shadow-sm ring-1 ring-black/5"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-amber-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span class="text-[11px] font-medium text-gray-800 tracking-tight">{{ t('contractCard.featured') }}</span>
          </div>

          <!-- Badge reserva online para colivings con bookingIntegration (ej: Homiii) -->
          <div
            v-if="hasDirectBooking"
            class="inline-flex items-center gap-1 pl-2 pr-2.5 py-1 rounded-full bg-indigo-600/90 backdrop-blur-sm shadow-sm"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
            <span class="text-[11px] font-medium text-white tracking-tight">Reserva online</span>
          </div>
          
          <!-- Match badge estilo Airbnb con score y tooltip -->
          <div 
            v-if="contract.matchData && contract.matchData.score >= 50 && !showValueBadge"
            class="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm shadow-sm ring-1 ring-black/5 text-[11px] font-medium text-gray-800 relative group cursor-help"
            :class="getMatchBadgeClass()"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 flex-shrink-0" :class="getMatchIconColor()" viewBox="0 0 20 20" fill="currentColor">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span>{{ getMatchScoreText() }}</span>
            
            <!-- Tooltip con razones de la recomendación -->
            <div class="absolute left-0 top-full mt-2 w-64 bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div class="font-semibold mb-2">{{ getMatchLevelText() }}</div>
              <ul v-if="contract.matchData.reasons && contract.matchData.reasons.length > 0" class="space-y-1">
                <li v-for="(reason, index) in contract.matchData.reasons" :key="index" class="flex items-start gap-1.5">
                  <svg class="h-3 w-3 text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                  </svg>
                  <span>{{ reason }}</span>
                </li>
              </ul>
              <div v-else class="text-gray-300">
                Coincide con tus preferencias de búsqueda
              </div>
              <!-- Flecha del tooltip -->
              <div class="absolute left-4 -top-1 w-2 h-2 bg-gray-900 transform rotate-45"></div>
            </div>
          </div>
        </div>
        
        <!-- Zona superior derecha: botones de acción [ojo] [compartir] [guardar] -->
        <div v-if="isColivingSearchResult" class="absolute top-3 right-3 flex items-center gap-1.5 z-10">
          <!-- Botón vista rápida (ojo) -->
          <button
            v-if="!isMobile"
            @click.prevent.stop="showQuickPreview = true"
            class="p-2 rounded-full focus:outline-none transition-all duration-200 focus:ring-0 bg-white/70 hover:bg-white hover:scale-110 hover:shadow-md"
            :title="t('contractCard.quickView')"
            :aria-label="t('contractCard.quickView')"
          >
            <svg class="h-4 w-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>

          <!-- Botón compartir -->
          <button
            @click.prevent.stop="handleShare"
            class="p-2 rounded-full focus:outline-none transition-all duration-200 focus:ring-0"
            :class="shareCopied
              ? 'bg-emerald-500 shadow-lg scale-110'
              : 'bg-white/70 hover:bg-white hover:scale-110 hover:shadow-md'"
            :title="shareCopied ? t('contractCard.linkCopied') : t('contractCard.shareColiving')"
            :aria-label="shareCopied ? t('contractCard.linkCopied') : t('contractCard.shareColiving')"
          >
            <svg v-if="!shareCopied" class="h-4 w-4 text-gray-700" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            <svg v-else class="h-4 w-4 text-white" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </button>

          <!-- Botón guardar (corazón) -->
          <button
            v-if="showSaveButton && contract.type === 'coliving'"
            @click.prevent.stop="handleToggleSave"
            class="p-2 rounded-full focus:outline-none transition-all duration-200 focus:ring-0"
            :class="isSaved
              ? 'bg-white shadow-lg scale-110'
              : 'bg-white/70 hover:bg-white hover:scale-110 hover:shadow-md'"
            :title="isSaved ? t('contractCard.removeFromSaved') : t('contractCard.saveColiving')"
            :aria-label="isSaved ? t('contractCard.removeFromSaved') : t('contractCard.saveColiving')"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              :class="isSaved ? 'text-red-500' : 'text-gray-700'"
              :fill="isSaved ? 'currentColor' : 'none'"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
        </div>

        <!-- Save button para contextos fuera de búsqueda (dashboard, etc.) -->
        <button
          v-if="!isColivingSearchResult && showSaveButton && contract.type === 'coliving'"
          @click.prevent.stop="handleToggleSave"
          class="absolute top-3 right-3 z-10 p-2.5 rounded-full focus:outline-none transition-all duration-200"
          :class="isSaved ? 'bg-white shadow-lg scale-110' : 'bg-white/70 hover:bg-white hover:scale-110 hover:shadow-md'"
          :title="isSaved ? t('contractCard.removeFromSaved') : t('contractCard.saveColiving')"
          :aria-label="isSaved ? t('contractCard.removeFromSaved') : t('contractCard.saveColiving')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            :class="isSaved ? 'text-red-500' : 'text-gray-700'"
            :fill="isSaved ? 'currentColor' : 'none'"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </button>
        
        <!-- Precio sobre la imagen - Inferior izquierda estilo Airbnb (RESTAURADO) -->
        <div v-if="shouldShowPrice || showSpacestPromoByPrice" class="absolute bottom-3 left-3 max-w-[92%]">
          <div class="bg-gray-500/75 backdrop-blur-sm px-2.5 py-1 rounded-md shadow-sm">
            <p v-if="shouldShowPrice" class="text-sm font-semibold text-white leading-snug">
              <span v-if="showFromLabel" class="font-normal">{{ t('common.from') }} </span>
              {{ displayPrice.toLocaleString() }}{{ currencySymbol }}
              <span v-if="contract.contractType === 'rent' || contract.type === 'coliving'" class="font-normal text-white/90 text-xs">{{ t('contractCard.perMonth') }}</span>
            </p>
            <p
              v-if="showSpacestPromoByPrice"
              class="text-[11px] font-semibold text-white leading-tight"
              :class="{ 'mt-0.5': shouldShowPrice }"
            >
              {{ spacestPromoByPriceLabel }}
            </p>
          </div>
        </div>
      </div>
      
      <!-- Contenido debajo de la imagen - Estilo Airbnb limpio y espaciado -->
      <div class="flex flex-col flex-grow pt-3" :class="[
        isMobile ? 'px-0' : 'px-0'
      ]">
        <!-- Ubicación + operador -->
        <div class="flex items-center gap-1.5 mb-0.5 min-w-0 flex-wrap">
          <p class="text-sm font-semibold text-gray-900 truncate">{{ contract.propertyLocation || 'España' }}</p>
          <template v-if="contract.isExternal">
            <span class="text-gray-400">·</span>
            <p class="text-sm text-gray-600 truncate">{{ capitalizeSource(contract.externalSource) }}</p>
          </template>
        </div>
        
        <!-- MEJORA E: Título optimizado (2 líneas max) -->
        <h3 class="font-semibold text-gray-900 line-clamp-2 text-base mb-1 leading-snug">
          {{ optimizedTitle }}
        </h3>

        <p
          v-if="affiliateGroupedInventoryLabel && isColivingSearchResult"
          class="text-xs font-medium text-emerald-700 mb-1.5"
        >
          {{ affiliateGroupedInventoryLabel }}
        </p>

        <!-- NIVEL 2 - B: COMPARATIVA DE MERCADO (debajo del título) -->
        <div v-if="marketComparisonText && isColivingSearchResult" class="flex items-center gap-1 mb-2">
          <svg class="w-3 h-3 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clip-rule="evenodd" />
          </svg>
          <span class="text-xs font-medium text-green-700">{{ marketComparisonText }}</span>
        </div>
        
        <!-- Características en una línea con puntos separadores - Estilo Airbnb -->
        <div class="flex items-center gap-1.5 text-sm text-gray-600 mb-1">
            <template v-if="contract.type === 'coliving' && isColivingSearchResult">
            <!-- Habitaciones disponibles formato compacto (Spacest agrupado: línea dedicada arriba) -->
            <template v-if="contract.totalRooms && !affiliateGroupedInventoryLabel">
              <span class="font-medium">{{ contract.availableRooms !== undefined ? `${contract.availableRooms}/${contract.totalRooms}` : contract.totalRooms }} {{ t('contractCard.roomsAvailable') }}</span>
              <span v-if="contract.bathrooms || contract.minimumStayMonths || (showActiveStayInfo && timeRemaining)" class="text-gray-400">·</span>
            </template>
            
            <!-- Baños con icono -->
            <template v-if="contract.bathrooms">
              <div class="flex items-center gap-1">
                <!-- Icono de baño/bañera -->
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 13h18"></path>
                  <path d="M3 13v4a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-4"></path>
                  <path d="M7 19v2"></path>
                  <path d="M17 19v2"></path>
                  <path d="M7 4v9"></path>
                  <path d="M7 4a2 2 0 0 1 2-2h2"></path>
                </svg>
                <span>{{ contract.bathrooms }}</span>
              </div>
              <span v-if="contract.minimumStayMonths || (showActiveStayInfo && timeRemaining)" class="text-gray-400">·</span>
          </template>

            <!-- Duración mínima -->
            <template v-if="contract.minimumStayMonths">
              <span>{{ t('contractCard.min') }} {{ contract.minimumStayMonths }} {{ contract.minimumStayMonths === 1 ? t('contractCard.month') : t('contractCard.months') }}</span>
              <span v-if="showActiveStayInfo && timeRemaining" class="text-gray-400">·</span>
          </template>
            </template>
            
          <!-- Para propiedades tradicionales - formato compacto -->
            <template v-else>
            <template v-if="contract.bedrooms">
              <span>{{ contract.bedrooms }} {{ t('contractCard.bedrooms') }}</span>
              <span v-if="contract.bathrooms || contract.propertySize || (showActiveStayInfo && timeRemaining)" class="text-gray-400">·</span>
              </template>
            <template v-if="contract.bathrooms">
              <span>{{ contract.bathrooms }} {{ t('contractCard.bathrooms') }}</span>
              <span v-if="contract.propertySize || (showActiveStayInfo && timeRemaining)" class="text-gray-400">·</span>
                </template>
            <template v-if="contract.propertySize">
              <span>{{ contract.propertySize }} m²</span>
              <span v-if="showActiveStayInfo && timeRemaining" class="text-gray-400">·</span>
              </template>
            </template>
            
            <!-- Tiempo restante (solo para estancias activas) -->
            <template v-if="showActiveStayInfo && timeRemaining">
              <span 
                class="font-medium"
                :class="timeRemaining.isUrgent ? 'text-amber-600' : 'text-gray-600'"
              >
                {{ timeRemaining.text }}
              </span>
            </template>
          </div>

        <!-- Servicios incluidos + valoración VeCy -->
        <div
          v-if="inclusionBadge || showVecyRating"
          class="flex items-center gap-1.5 flex-wrap min-w-0"
        >
          <span
            v-if="inclusionBadge"
            :class="['text-xs font-medium', inclusionBadge.class]"
          >
            {{ inclusionBadge.text }}
          </span>
          <span v-if="inclusionBadge && showVecyRating" class="text-gray-400 text-xs">·</span>
          <VecyLocationRating
            v-if="showVecyRating"
            inline
            :score="vecyRating.score"
            :review-count="vecyRating.reviewCount"
            size="sm"
            :show-review-count="false"
          />
        </div>
        
        <!-- Información de contacto del agente (solo para estancias activas) -->
        <div 
          v-if="showActiveStayInfo && timeRemaining" 
          class="mt-2 space-y-1.5"
        >
          <!-- Fecha de finalización compacta -->
          <div class="flex items-center gap-1.5 text-xs text-gray-500">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              class="h-3.5 w-3.5 flex-shrink-0"
              :class="timeRemaining.isUrgent ? 'text-amber-500' : 'text-gray-400'"
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>{{ t('contractCard.endsOn') }} {{ formatDateLong(timeRemaining.endDate) }}</span>
          </div>
          
          <!-- Email y Teléfono del agente en la misma fila -->
          <div v-if="contract.agentEmail || contract.agentPhone" class="flex items-center gap-3 text-xs">
            <!-- Email -->
            <a 
              v-if="contract.agentEmail"
              :href="`mailto:${contract.agentEmail}`" 
              class="flex items-center gap-1.5 text-gray-600 hover:text-gray-800 hover:underline truncate flex-1"
              @click.stop
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                class="h-3.5 w-3.5 flex-shrink-0 text-gray-400" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span class="truncate">{{ contract.agentEmail }}</span>
            </a>
            
            <!-- Teléfono -->
            <a 
              v-if="contract.agentPhone"
              :href="`tel:${contract.agentPhone}`" 
              class="flex items-center gap-1.5 text-gray-600 hover:text-gray-800 hover:underline flex-shrink-0"
              @click.stop
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                class="h-3.5 w-3.5 flex-shrink-0 text-gray-400" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span>{{ contract.agentPhone }}</span>
            </a>
          </div>
        </div>
        
        <!-- Status badges - SOLO para dashboard del usuario (no para búsqueda) -->
        <div v-if="!isColivingSearchResult && contract.applicationStatus" class="flex flex-wrap gap-2 mt-2">
          <span 
            :class="[
              'px-2.5 py-1 rounded-full text-xs font-medium',
              contract.statusClass || getDefaultStatusClass()
            ]"
          >
            {{ contract.statusText || getDefaultStatusText() }}
          </span>
        </div>

        <!-- Botón retirar solicitud (solo en Contracts.vue, pestaña "Tus solicitudes") -->
        <div v-if="showWithdrawButton && isWithdrawableColiving" class="mt-3">
          <button
            @click.prevent.stop="handleWithdraw"
            :disabled="isWithdrawing"
            class="w-full flex justify-center items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-gray-200 text-gray-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50/50 transition-all duration-200 disabled:opacity-50"
          >
            <svg v-if="isWithdrawing" class="animate-spin w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <svg v-else class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            {{ isWithdrawing ? 'Retirando...' : 'Retirar solicitud' }}
          </button>
        </div>
      </div>
    </component>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[1001]">
      <div class="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl border border-gray-100">
        <h3 class="text-lg font-bold mb-4 text-gray-800">{{ t('contractCard.deleteModal.title') }}</h3>
        <p class="mb-6 text-gray-600">{{ t('contractCard.deleteModal.message') }}</p>
        <div class="flex justify-end space-x-3">
          <button 
            @click="closeDeleteModal" 
            class="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200 font-medium"
          >
            {{ t('common.cancel') }}
          </button>
          <button 
            @click="confirmDelete" 
            class="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-md hover:from-red-600 hover:to-red-700 transition-all duration-200 font-medium shadow-sm"
          >
            {{ t('contractCard.deleteModal.delete') }}
          </button>
        </div>
      </div>
    </div>

    <!-- NIVEL 2 - D: Modal Quick Preview (con diseño de ContractCardModal) -->
    <div v-if="showQuickPreview" class="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[1001]" @click.self="showQuickPreview = false">
      <div class="bg-white w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl shadow-xl overflow-hidden animate-modal-in max-h-[90vh] flex flex-col">
        <!-- Header limpio con imagen del coliving -->
        <div class="relative h-48 sm:h-56 bg-gray-100 flex-shrink-0">
          <!-- Imagen de fondo si existe -->
          <div 
            v-if="firstImageUrl"
            class="absolute inset-0 bg-cover bg-center"
            :style="{ backgroundImage: `url('${firstImageUrl}')` }"
          ></div>
          <!-- Overlay sutil -->
          <div class="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
          
          <!-- Botón cerrar estilo Airbnb -->
          <button 
            @click="showQuickPreview = false"
            class="absolute top-3 right-3 p-2 bg-white rounded-full shadow-md"
          >
            <svg class="h-4 w-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
          
          <!-- Badge del proveedor/tipo (si es externo) -->
          <div v-if="contract.isExternal" class="absolute top-3 left-3">
            <span class="px-2.5 py-1 bg-white text-gray-700 text-xs font-medium rounded-lg shadow-sm">
              {{ capitalizeSource(contract.externalSource) }}
            </span>
          </div>
          
          <!-- Título y ubicación sobre la imagen -->
          <div class="absolute bottom-0 left-0 right-0 px-4 pb-4">
            <h3 class="text-base sm:text-lg font-semibold text-white mb-1 line-clamp-1">{{ optimizedTitle }}</h3>
            <p class="text-white/90 text-sm flex items-center">
              <svg class="h-4 w-4 mr-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
              </svg>
              {{ contract.propertyLocation }}
            </p>
          </div>
        </div>
        
        <!-- Contenido del modal con scroll -->
        <div class="px-4 py-5 sm:p-6 overflow-y-auto flex-1">
          <!-- Precio y habitaciones -->
          <div class="flex items-center justify-between mb-6 pb-5 border-b border-gray-200">
            <div>
              <p class="text-xs text-gray-500 mb-1">{{ t('contractCard.quickPreview.priceFrom') }}</p>
              <p class="text-2xl font-semibold text-gray-900">{{ displayPrice.toLocaleString() }}{{ currencySymbol }}<span class="text-sm font-normal text-gray-600">{{ t('contractCard.perMonth') }}</span></p>
              <p v-if="showSpacestPromoByPrice" class="text-xs font-medium text-gray-800 mt-1">
                {{ spacestPromoByPriceLabel }}
              </p>
            </div>
            <div class="text-right">
              <p class="text-xs text-gray-500 mb-1">{{ t('contractCard.quickPreview.rooms') }}</p>
              <p class="text-lg font-semibold text-gray-900">{{ contract.totalRooms || '—' }}</p>
            </div>
          </div>
        
          <!-- Servicios incluidos (si existen) -->
          <div v-if="contract.servicesInternet || contract.servicesCleaning || contract.minimumStayMonths" class="mb-5">
            <h4 class="text-sm font-semibold text-gray-900 mb-3">{{ t('contractCard.quickPreview.servicesIncluded') }}</h4>
            <div class="flex flex-wrap gap-2">
              <span v-if="contract.servicesInternet" class="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg">
                {{ t('contractCard.wifi') }}
              </span>
              <span v-if="contract.servicesCleaning" class="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg">
                {{ t('contractCard.cleaning') }}
              </span>
              <span v-if="contract.servicesElectricity && contract.servicesWaterAndWaste" class="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg">
                {{ t('contractCard.quickPreview.expenses') }}
              </span>
              <span v-if="contract.minimumStayMonths" class="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg">
                {{ t('contractCard.quickPreview.minStay', contract.minimumStayMonths) }}
              </span>
            </div>
          </div>
        
          <!-- Estadísticas comparativas de mercado -->
          <div v-if="contract.marketComparison" class="mb-5">
            <div class="flex items-center justify-between mb-3">
              <h4 class="text-sm font-semibold text-gray-900">{{ t('contractCard.quickPreview.marketComparison') }}</h4>
              <span class="text-xs text-gray-500">{{ t('contractCard.quickPreview.colivingsCount', { count: contract.marketComparison.marketContext?.totalInLocation || 0 }) }}</span>
            </div>
        
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <!-- Precio -->
              <div class="flex items-start gap-3">
                <div class="text-2xl">💰</div>
                <div class="flex-1 min-w-0">
                  <p class="text-xs text-gray-500 mb-0.5">{{ t('contractCard.quickPreview.priceLabel') }}</p>
                  <p class="text-sm font-medium text-gray-900 truncate">{{ getMarketComparisonTitle(contract.marketComparison.priceRank) }}</p>
                </div>
              </div>
              
              <!-- Centralidad (si existe) -->
              <div v-if="contract.marketComparison.centralityRank" class="flex items-start gap-3">
                <div class="text-2xl">📍</div>
                <div class="flex-1 min-w-0">
                  <p class="text-xs text-gray-500 mb-0.5">{{ t('contractCard.quickPreview.locationLabel') }}</p>
                  <p class="text-sm font-medium text-gray-900 truncate">{{ getCentralityTitle(contract.marketComparison.centralityRank) }}</p>
                </div>
              </div>
              
              <!-- Servicios -->
              <div class="flex items-start gap-3">
                <div class="text-2xl">✨</div>
                <div class="flex-1 min-w-0">
                  <p class="text-xs text-gray-500 mb-0.5">{{ t('contractCard.quickPreview.servicesLabel') }}</p>
                  <p class="text-sm font-medium text-gray-900 truncate">{{ getServicesTitle(contract.marketComparison.servicesRank) }}</p>
                </div>
              </div>
              
              <!-- Flexibilidad -->
              <div class="flex items-start gap-3">
                <div class="text-2xl">📅</div>
                <div class="flex-1 min-w-0">
                  <p class="text-xs text-gray-500 mb-0.5">{{ t('contractCard.quickPreview.flexibilityLabel') }}</p>
                  <p class="text-sm font-medium text-gray-900 truncate">{{ getFlexibilityTitle(contract.marketComparison.flexibilityRank) }}</p>
                </div>
              </div>
            </div>
            
            <!-- Puntos destacados -->
            <div v-if="contract.marketComparison.highlights && contract.marketComparison.highlights.length > 0" class="mt-4 pt-4 border-t border-gray-200">
              <p class="text-xs text-gray-500 mb-2">{{ t('contractCard.quickPreview.highlights') }}</p>
              <div class="flex flex-wrap gap-2">
                <span v-for="highlight in contract.marketComparison.highlights" :key="highlight"
                      class="inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-700">
                  {{ getHighlightLabel(highlight) }}
                </span>
              </div>
            </div>
          </div>
        
          <!-- Mensaje informativo (si es externo sin booking integration) -->
          <div v-if="contract.isExternal && !hasDirectBooking" class="bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
            <p class="text-sm text-gray-600" v-html="t('contractCard.quickPreview.redirectMessage', { provider: `<span class=\'font-semibold text-gray-900\'>${capitalizeSource(contract.externalSource)}</span>` })"></p>
          </div>
          <!-- Mensaje informativo para booking integration (se reserva en Coolliving) -->
          <div v-else-if="hasDirectBooking" class="bg-indigo-50 rounded-lg px-4 py-3 border border-indigo-100">
            <p class="text-sm text-indigo-700">
              <span class="font-semibold">Reserva directamente</span> desde la ficha del coliving en Coolliving. Sin redirecciones.
            </p>
          </div>
        </div>
        
        <!-- Footer con botones -->
        <div class="px-4 py-4 sm:px-6 border-t border-gray-200 bg-white flex-shrink-0">
          <div class="flex flex-col sm:flex-row gap-3">
            <button 
              @click="showQuickPreview = false" 
              class="px-5 py-3 bg-white border border-gray-300 text-gray-600 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              {{ t('contractCard.quickPreview.back') }}
            </button>
            
            <!-- Botón para ver todos los colivings del proveedor (solo externos) -->
            <router-link
              v-if="contract.isExternal"
              :to="`/encuentratucoliving/${normalizeProviderSlug(contract.externalSource)}`"
              @click="showQuickPreview = false"
              class="px-5 py-3 bg-white border border-indigo-200 text-indigo-600 rounded-full text-sm font-medium flex items-center justify-center gap-2 hover:bg-indigo-50 hover:shadow-md shadow-sm transition-all"
            >
              <span class="hidden sm:inline">{{ t('contractCard.quickPreview.moreFrom', { provider: capitalizeSource(contract.externalSource) }) }}</span>
              <span class="sm:hidden">{{ t('contractCard.quickPreview.more') }}</span>
            </router-link>
            
              <button 
              @click="handleQuickPreviewCTA" 
              class="flex-1 px-5 py-3 bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-50 hover:shadow-md shadow-sm rounded-full text-sm font-medium flex items-center justify-center gap-2 transition-all"
            >
              <span>{{ (contract.isExternal && !hasDirectBooking) ? t('contractCard.quickPreview.viewIn', { provider: capitalizeSource(contract.externalSource) }) : t('contractCard.quickPreview.viewDetails') }}</span>
            </button>
          </div>
          <!-- Disclaimer (solo para externos) -->
          <p v-if="contract.isExternal" class="text-xs text-gray-500 text-center mt-3">
            {{ t('contractCard.quickPreview.notManaged') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Modal externo (componente separado) -->
    <ContractCardModal 
      :is-visible="showExternalModal"
      :contract="contract"
      :first-image-url="firstImageUrl"
      :provider-name="capitalizeSource(contract.externalSource)"
      :provider-slug="normalizeProviderSlug(contract.externalSource)"
      @close="closeExternalModal"
      @go-to-external="goToExternalUrl"
    />
  </div>
</template>

<script>
import { computed, ref, watch, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { generatePublicUrl, convertToPublicUrls } from '../utils/imageUtils'
import { resolveColivingImageUrl } from '../utils/colivingImageUrl'
import { getColivingListImageUrl } from '../utils/colivingListImageUrl'
import {
  openExternalColiving,
  openAffiliatePartnerListing,
  isAffiliatePartnerListing,
  isBluegroundAffiliateListing,
  isSpacestPartnerListing,
  resolveExternalApplyUrl,
  resolveExternalProvider,
} from '../utils/externalRedirect'
import { addUtmParams } from '../utils/addUtmParams'
import ContractCardModal from './ContractCardModal.vue'
import { useRegion } from '@/composables/useRegion'
import { useAnalytics } from '@/composables/useAnalytics'
import { getColivingInclusionLevel, buildInclusionBadge, INCLUSION_LEVELS } from '@/utils/colivingInclusion'
import { resolveSpacestSearchDisplayTitle } from '@/utils/colivingAddressUtils'
import { getSpacestPromoCopy } from '@/utils/spacestAffiliate'
import { hasDirectBooking as checkDirectBooking } from '@/config/bookingIntegration'
import VecyLocationRating from './VecyLocationRating.vue'

const PROVIDER_SLUG_MAP = {
  kanso: 'kanso',
  gaiastays: 'gaiastays',
  urbancampus: 'urbancampus',
  muppy: 'muppy',
  enso: 'enso',
  haaus: 'haaus',
  aticco: 'aticco',
  socien: 'socien',
  spacest: 'spacest',
  suiters: 'suiters',
  houxury: 'houxury',
  colivinn: 'colivinn',
  trc: 'trc',
  trees: 'trees',
  habyt: 'habyt',
  outsite: 'outsite',
  olivar: 'olivar',
  livensa: 'livensa',
  nomad: 'nomad',
  maraya: 'maraya',
  colive: 'colive',
  liveit: 'liveit',
  socialhub: 'socialhub',
  becasa: 'becasa',
  alegoria: 'alegoria',
  yugo: 'yugo',
  thestart: 'thestart',
  flaternity: 'flaternity',
  hive: 'hive',
  repeople: 'repeople',
  homiii: 'homiii',
  node: 'node',
  flipco: 'flipco',
  queens: 'queens',
  mycoliving: 'mycoliving',
  nova: 'nova',
  resa: 'resa',
  collegiate: 'collegiate',
  farhome: 'farhome',
  malagacolivings: 'malagacolivings',
  sevillacolivings: 'sevillacolivings',
  aspasios: 'aspasios',
}

const SOURCE_DISPLAY_NAMES = {
  kanso: 'Kanso Coliving',
  gaiastays: 'GaiaStays',
  urbancampus: 'Urban Campus',
  muppy: 'Muppy',
  enso: 'Enso Coliving',
  haaus: 'Haaus',
  aticco: 'Aticco Living',
  socien: 'Socien',
  spacest: 'Spacest',
  suiters: 'Suiters',
  houxury: 'Houxury',
  colivinn: 'ColivINN',
  trc: 'The Residential Club',
  node: 'Node Living',
  homiii: 'Homiii',
  colive: 'Colive',
  trees: 'Trees Coliving',
  resa: 'RESA',
  yugo: 'Yugo',
  livensa: 'Livensa Living',
  collegiate: 'Collegiate',
  spotahome: 'Spotahome',
  badi: 'Badi',
  idealista: 'Idealista',
  flaternity: 'Flaternity',
  hive: 'Grupo Hive',
  flipco: 'Flipco',
  liveit: 'Live It',
  habyt: 'Habyt',
  queens: 'Queens',
  socialhub: 'The Social Hub',
  outsite: 'Outsite',
  mycoliving: 'MycoLiving',
  becasa: 'Becasa',
  oceanika: 'Oceanika',
  nova: 'Nova Coliving',
  alegoria: 'Alegoría',
  olivar: 'Olivar Plaza',
  thestart: 'The Start',
  nomad: 'Nomad Flex Living',
  farhome: 'Far Home',
  repeople: 'Repeople',
  maraya: 'Maraya',
  aspasios: 'Aspasios Boutique Apartments',
  iq: 'iQ Student',
  unite: 'Unite Students',
  bloom: 'Bloom Coliving',
  bamboo: 'Bamboo Living',
  stayclub: 'The Stay Club',
  dandi: 'Dandi Living',
  uncle: 'UNCLE',
  yourtribe: 'YourTRIBE',
  scape: 'Scape Student',
  thecollective: 'The Collective',
  studentroost: 'Student Roost',
  domicoliving: 'Domi Coliving',
  chapter: 'Chapter Living',
  aparto: 'aparto',
  vonder: 'Vonder',
  sharedeasy: 'SharedEasy',
}

function processListingImageUrl(imageUrl) {
  if (!imageUrl || typeof imageUrl !== 'string') return ''
  if (imageUrl.startsWith('https://') || imageUrl.startsWith('http://')) {
    return resolveColivingImageUrl(imageUrl)
  }
  const processedUrl = generatePublicUrl(imageUrl) || imageUrl
  return processedUrl ? encodeURI(processedUrl) : ''
}

const CAROUSEL_ADVANCE_MS = 1500

export default {
  name: 'ContractCard',
  components: {
    ContractCardModal,
    VecyLocationRating,
  },
  props: {
    contract: {
      type: Object,
      required: true
    },
    userType: {
      type: String,
      default: null
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    currentUserId: {
      type: String,
      default: null
    },
    isCurrentUserInterested: {
      type: Boolean,
      default: false
    },
    isColivingSearchResult: {
      type: Boolean,
      default: false
    },
    isSaved: {
      type: Boolean,
      default: false
    },
    showSaveButton: {
      type: Boolean,
      default: false
    },
    showActiveStayInfo: {
      type: Boolean,
      default: false
    },
    showWithdrawButton: {
      type: Boolean,
      default: false
    },
    resultIndex: {
      type: Number,
      default: -1
    },
    vecyRating: {
      type: Object,
      default: null
    }
  },
  emits: ['delete', 'toggleSave', 'withdraw', 'request-scraped-lead', 'request-hive-lead'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const router = useRouter()
    const { regionId } = useRegion()
    const { trackShareColiving, trackViewColiving, trackScrapedLeadIntent } = useAnalytics()

    // Símbolo de moneda según la región activa
    const currencySymbol = computed(() => {
      if (regionId.value === 'us') return '$'
      if (regionId.value === 'uk') return '£'
      return '€'
    })

    const hasDirectBooking = computed(() => checkDirectBooking(props.contract))

    const showDeleteModal = computed(() => false) // You'll need to manage this state

    // Estado para modal de redirección externa
    const showExternalModal = ref(false)

    // Estado para botón retirar solicitud
    const isWithdrawing = ref(false)
    
    // NIVEL 2 - D: Estado para quick preview modal
    const showQuickPreview = ref(false)

    // Compartir coliving desde la card
    const shareCopied = ref(false)
    let shareCopiedTimer = null

    const getShareUrl = () => {
      const baseUrl = window.location.origin
      // Colivings con booking integration: compartir la ficha de detalle
      if (hasDirectBooking.value) {
        return `${baseUrl}/colivings/${props.contract.bookingIntegration}/${props.contract.id}`
      }
      // Colivings scrapeados sin booking: compartir la página del operador
      if (props.contract.isExternal) {
        const providerSlug = normalizeProviderSlug(props.contract.externalSource)
        const prefix = regionId.value === 'us' || regionId.value === 'uk'
          ? 'findyourcoliving'
          : 'encuentratucoliving'
        return `${baseUrl}/${prefix}/${providerSlug}`
      }
      // Coliving propio: enlace directo a la ficha
      const path = props.contract.type === 'coliving'
        ? `/colivings/${props.contract.id}`
        : `/contracts/${props.contract.id}`
      return `${baseUrl}${path}`
    }

    const handleShare = () => {
      const url = getShareUrl()
      const doTrack = () => {
        shareCopied.value = true
        clearTimeout(shareCopiedTimer)
        shareCopiedTimer = setTimeout(() => { shareCopied.value = false }, 2000)
        trackShareColiving({
          colivingId: props.contract.id,
          ciudad: props.contract.propertyLocation || '',
          method: 'copy',
        })
      }
      navigator.clipboard.writeText(url).then(doTrack).catch(() => {
        const ta = document.createElement('textarea')
        ta.value = url
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        doTrack()
      })
    }

    const isWithdrawableColiving = computed(() => {
      if (props.contract.type !== 'coliving') return false
      const withdrawableStatuses = ['submitted', 'pending', 'under_review', 'pre_approved', 'owner_signed', 'applicant_signed', 'contract_signed', 'rejected']
      return withdrawableStatuses.includes(props.contract.applicationStatus)
    })

    const handleWithdraw = () => {
      emit('withdraw', props.contract.id)
    }
    
    // NIVEL 2 - D: Manejar CTA de quick preview
    const hasAffiliatePartner = computed(() =>
      !hasDirectBooking.value && isAffiliatePartnerListing(props.contract),
    )

    const externalRedirectUrl = computed(() => resolveExternalApplyUrl(props.contract))

    const openAffiliatePartnerRedirect = () => {
      openAffiliatePartnerListing(props.contract, {
        userId: props.currentUserId,
        source: 'coliving-search',
      })
    }

    const handleQuickPreviewCTA = () => {
      showQuickPreview.value = false
      trackSearchClick()

      if (hasDirectBooking.value) {
        notifyExternalClick()
        router.push(`/colivings/${props.contract.bookingIntegration}/${props.contract.id}`)
      } else if (hasAffiliatePartner.value) {
        openAffiliatePartnerRedirect()
      } else if (props.contract.isHivePartner) {
        emit('request-hive-lead', props.contract)
      } else if (props.contract.isExternal) {
        notifyExternalClick()
        trackScrapedLeadIntent({
          colivingId: props.contract.id,
          colivingNombre: props.contract.title || props.contract.propertyTitle || props.contract.externalSource || '',
          ciudad: props.contract.propertyLocation || props.contract.city || '',
          fuente: props.contract.externalSource || '',
          origen: 'quick-preview',
        })
        emit('request-scraped-lead', props.contract)
      } else {
        const route = props.contract.type === 'coliving'
          ? `/colivings/${props.contract.id}`
          : `/contracts/${props.contract.id}`
        router.push(route)
      }
    }
    
    // NIVEL 2 - D: Funciones auxiliares para el modal (marketComparison)
    const getMarketComparisonTitle = (priceRank) => {
      const key = `contractCard.quickPreview.priceRank.${priceRank}`
      const translated = t(key)
      return translated !== key ? translated : t('contractCard.quickPreview.priceLabel')
    }
    
    const getCentralityTitle = (centralityRank) => {
      const key = `contractCard.quickPreview.centralityRank.${centralityRank}`
      const translated = t(key)
      return translated !== key ? translated : t('contractCard.quickPreview.locationLabel')
    }
    
    const getServicesTitle = (servicesRank) => {
      const key = `contractCard.quickPreview.servicesRank.${servicesRank}`
      const translated = t(key)
      return translated !== key ? translated : t('contractCard.quickPreview.servicesLabel')
    }
    
    const getFlexibilityTitle = (flexibilityRank) => {
      const key = `contractCard.quickPreview.flexibilityRank.${flexibilityRank}`
      const translated = t(key)
      return translated !== key ? translated : t('contractCard.quickPreview.flexibilityLabel')
    }
    
    const getHighlightLabel = (highlight) => {
      const key = `contractCard.quickPreview.highlightLabel.${highlight}`
      const translated = t(key)
      return translated !== key ? translated : highlight
    }
    
    // Computed: tipo de componente para el wrapper de la tarjeta
    // Externos con bookingIntegration → 'a' (navega a detail page, como nativo)
    // Externos sin bookingIntegration → 'div' (abre modal de lead)
    // Nativos → 'a'
    const cardLinkComponent = computed(() => {
      if (props.contract.isExternal && !hasDirectBooking.value) {
        return 'div'
      }
      if (props.contract.isHivePartner) {
        return 'div'
      }
      return 'a'
    })
    
    // Computed: URL de destino para la tarjeta
    const cardHref = computed(() => {
      if (props.contract.isExternal && !hasDirectBooking.value) {
        return undefined
      }
      if (props.contract.isHivePartner) {
        return undefined
      }
      // Externos con booking integration → ruta /colivings/:source/:externalId
      if (hasDirectBooking.value) {
        return `/colivings/${props.contract.bookingIntegration}/${props.contract.id}`
      }
      return props.contract.type === 'coliving' 
        ? `/colivings/${props.contract.id}` 
        : `/contracts/${props.contract.id}`
    })
    
    // Computed: ruta para router-link (no se usa actualmente, pero disponible)
    const cardTo = computed(() => {
      return undefined
    })
    
    const trackSearchClick = () => {
      if (!props.isColivingSearchResult) return
      try {
        const payload = {
          colivingId: props.contract.id,
          colivingTitle: props.contract.title || props.contract.propertyTitle || '',
          isExternal: !!props.contract.isExternal,
          position: props.resultIndex >= 0 ? props.resultIndex : undefined,
          userId: props.currentUserId || null,
          city: props.contract.propertyLocation || null,
        }
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
        if (navigator.sendBeacon) {
          navigator.sendBeacon('/api/analytics/search-click', blob)
        } else {
          fetch('/api/analytics/search-click', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload), keepalive: true,
          }).catch(() => { /* non-blocking */ })
        }
      } catch { /* non-blocking */ }
    }
    
    const handleCardClick = (event) => {
      if (event.target.closest('button')) {
        event.preventDefault()
        return
      }

      trackSearchClick()
      
      // Externos con booking integration → navegar a detail page (como nativo)
      if (hasDirectBooking.value) {
        if (event.ctrlKey || event.metaKey) return
        event.preventDefault()
        notifyExternalClick()
        router.push(`/colivings/${props.contract.bookingIntegration}/${props.contract.id}`)
        return
      }
      
      // Partner afiliado → modal email + redirect (Outsite / BG / Spacest / AYA)
      if (hasAffiliatePartner.value) {
        event.preventDefault()
        event.stopPropagation()
        openAffiliatePartnerRedirect()
        return
      }

      // Externos sin booking integration → modal de lead (comportamiento original)
      if (props.contract.isExternal) {
        event.preventDefault()
        notifyExternalClick()
        // Micro-conversión: usuario hace click en un coliving externo → intención de contacto
        trackScrapedLeadIntent({
          colivingId: props.contract.id,
          colivingNombre: props.contract.title || props.contract.propertyTitle || props.contract.externalSource || '',
          ciudad: props.contract.propertyLocation || props.contract.city || '',
          posicion: props.resultIndex >= 0 ? props.resultIndex : undefined,
          fuente: props.contract.externalSource || '',
        })
        emit('request-scraped-lead', props.contract)
        return
      }

      // Partner Hive → modal de lead dedicado
      if (props.contract.isHivePartner) {
        event.preventDefault()
        emit('request-hive-lead', props.contract)
        return
      }
      
      if (event.ctrlKey || event.metaKey) return
      
      event.preventDefault()
      const route = props.contract.type === 'coliving' 
        ? `/colivings/${props.contract.id}` 
        : `/contracts/${props.contract.id}`
      
      router.push(route)
    }
    
    // Función para abrir modal de redirección externa
    const openExternalModal = () => {
      if (props.contract.isExternal) {
        showExternalModal.value = true
      }
    }
    
    // Función para cerrar modal
    const closeExternalModal = () => {
      showExternalModal.value = false
    }
    
    // Función para ir a URL externa
    const goToExternalUrl = () => {
      if (hasAffiliatePartner.value) {
        openAffiliatePartnerRedirect()
      } else {
        const url = externalRedirectUrl.value
        if (url) {
          openExternalColiving({
            url,
            provider: resolveExternalProvider(props.contract),
            colivingId: props.contract.id,
            colivingTitle: props.contract.title || props.contract.propertyTitle || '',
            userId: props.currentUserId,
            city: props.contract.propertyLocation,
            source: 'coliving-search',
          })
        }
      }
      closeExternalModal()
    }
    
    // Registro + email admin vía POST /api/out/notify (externalClickLogs + jaime@coolliving.io)
    const notifyExternalClick = () => {
      if (hasAffiliatePartner.value) return
      const url = externalRedirectUrl.value
      const params = new URLSearchParams({
        url,
        provider: resolveExternalProvider(props.contract),
        colivingId: props.contract.id || '',
        colivingTitle: props.contract.title || props.contract.propertyTitle || '',
        userId: props.currentUserId || '',
        city: props.contract.propertyLocation || '',
        source: 'coliving-search',
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      fetch(`/api/out/notify?${params.toString()}`, { method: 'POST', keepalive: true }).catch(() => {})
    }

    // Función para normalizar el slug del proveedor (debe coincidir con el router)
    const normalizeProviderSlug = (source) => {
      if (!source) return ''
      const normalized = source.toLowerCase().trim()
      return PROVIDER_SLUG_MAP[normalized] || normalized
    }

    const capitalizeSource = (source) => {
      if (!source) return t('contractCard.external')
      const key = source.toLowerCase()
      return SOURCE_DISPLAY_NAMES[key] || source.charAt(0).toUpperCase() + source.slice(1)
    }
    
    // Computed property para el precio mostrado
    const displayPrice = computed(() => {
      if (props.contract.type === 'coliving') {
        // Para colivings externos/scrapeados, mostrar siempre el precio
        if (props.contract.isExternal) {
          return props.contract.propertyPrice || props.contract.priceFrom || null
        }
        
        // Para colivings en búsqueda (sin applicationStatus), mostrar priceFrom o propertyPrice
        // Esto aplica tanto a colivings propios como scrapeados en la lista de resultados
        if (props.isColivingSearchResult && !props.contract.applicationStatus) {
          return props.contract.priceFrom || props.contract.propertyPrice || null
        }
        
        const status = props.contract.applicationStatus || 'pending'
        
        // Si está pendiente o terminado, no mostrar precio (en dashboard de candidaturas)
        if (status === 'pending' || status === 'terminated') {
          return null
        }
        
        // Si tiene habitación asignada, mostrar el precio de esa habitación
        if (status === 'room_assigned' && props.contract.applicationData?.assignedRoomData?.price) {
          return props.contract.applicationData.assignedRoomData.price
        }
        
        // Para otros estados (accepted, rejected), verificar si hay precio disponible
        if (status === 'accepted' || status === 'rejected') {
          // Si tiene habitación asignada, usar su precio
          if (props.contract.applicationData?.assignedRoomData?.price) {
            return props.contract.applicationData.assignedRoomData.price
          }
          // Sino, usar precio base del coliving o el precio que viene del backend
          return props.contract.basePrice || props.contract.propertyPrice || null
        }
        
        // Para cualquier otro estado, usar lo que venga del backend
        return props.contract.propertyPrice
      } else {
        // Para contratos tradicionales, usar el precio normal
        return props.contract.propertyPrice
      }
    })
    
    // Computed property para determinar si mostrar el precio
    const shouldShowPrice = computed(() => {
      return displayPrice.value !== null && displayPrice.value !== undefined && displayPrice.value > 0
    })
    
    // Computed para determinar si mostrar "Desde" antes del precio
    // Se muestra cuando es un coliving (externo o propio) con múltiples habitaciones
    const showFromLabel = computed(() => {
      if (props.contract.type !== 'coliving') return false
      
      // Para colivings externos, siempre mostrar "Desde"
      if (props.contract.isExternal) return true
      
      // Para colivings propios en búsqueda con múltiples habitaciones disponibles
      if (props.isColivingSearchResult && !props.contract.applicationStatus) {
        const totalRooms = props.contract.totalRooms || props.contract.roomsConfiguration?.length || 0
        return totalRooms > 1
      }
      
      return false
    })
    
    // Computed para verificar si hay servicios incluidos
    const hasIncludedServices = computed(() => {
      if (props.contract.type !== 'coliving') return false
      return props.contract.servicesInternet || 
             props.contract.servicesCleaning || 
             hasAllServicesIncluded.value
    })
    
    // Computed para verificar si todos los servicios están incluidos
    const hasAllServicesIncluded = computed(() => {
      if (props.contract.type !== 'coliving') return false
      return getColivingInclusionLevel(props.contract) === INCLUSION_LEVELS.ALL_INCLUDED
    })

    const inclusionBadge = computed(() => {
      if (props.contract.type !== 'coliving' || !props.isColivingSearchResult) return null
      return buildInclusionBadge(props.contract, {
        allIncluded: t('contractCard.inclusion.allIncluded'),
      })
    })

    const showVecyRating = computed(
      () => props.isColivingSearchResult && props.vecyRating !== null,
    )
    
    // Función para obtener la clase CSS del badge de tipo
    const getTypeBadgeClass = () => {
      if (props.contract.type === 'coliving') {
        // Si es una residencia de estudiantes, usar color diferente
        if (props.contract.isExternal && props.contract.forStudents) {
          return 'bg-amber-500/80 text-white'
        }
        return 'bg-purple-500/80 text-white'
      }
      return 'bg-indigo-500/80 text-white'
    }
    
    // Función para obtener el texto del badge de tipo
    const getTypeBadgeText = () => {
      if (props.contract.displayType) {
        return props.contract.displayType
      }
      
      if (props.contract.type === 'coliving') {
        // Si es un coliving externo y es para estudiantes, mostrar "Residencia"
        if (props.contract.isExternal && props.contract.forStudents) {
          return t('contractCard.residence')
        }
        return t('contractCard.coliving')
      }
      
      return t('contractCard.property')
    }
    
    const getUserRoleBadge = () => {
      if (props.contract.type === 'coliving') {
        return t('contractCard.applicant')
      } else {
        return props.userType === 'owner' ? t('contractCard.owner') : t('contractCard.interested')
      }
    }
    
    const getDefaultStatusClass = () => {
      if (props.contract.type === 'coliving') {
        const status = props.contract.applicationStatus || 'pending'
        switch (status) {
          case 'room_assigned':
            return 'bg-black text-white'
          case 'pre_approved':
            return 'bg-gray-600 text-white'
          case 'contract_signed':
            return 'bg-gray-600 text-white'
          case 'payment_completed':
            return 'bg-gray-600 text-white'
          case 'accepted':
            return 'bg-gray-600 text-white'
          case 'rejected':
            return 'bg-black text-white'
          case 'terminated':
            return 'bg-gray-600 text-white'
          case 'pending':
          default:
            return 'bg-gray-600 text-white'
        }
      } else {
        return props.contract.assignedApplicant 
          ? 'bg-gray-600 text-white'
          : 'bg-gray-600 text-white'
      }
    }
    
    const getDefaultStatusText = () => {
      if (props.contract.type === 'coliving') {
        const status = props.contract.applicationStatus || 'pending'
        switch (status) {
          case 'room_assigned':
            return t('contractCard.roomAssigned')
          case 'pre_approved':
            return t('contractCard.preApproved')
          case 'contract_signed':
            return t('contractCard.contractSigned')
          case 'payment_completed':
            return t('contractCard.paymentCompleted')
          case 'accepted':
            return t('contractCard.accepted')
          case 'rejected':
            return t('contractCard.rejected')
          case 'terminated':
            return t('contractCard.terminated')
          case 'pending':
          default:
            return t('contractCard.pending')
        }
      } else {
        return props.contract.assignedApplicant ? t('contractCard.assigned') : t('contractCard.statusAvailable')
      }
    }
    
    const getOwnerInfo = (ownerInfo) => {
      if (!ownerInfo) return t('contractCard.ownerInfoNotAvailable')
      return ownerInfo.displayName || ownerInfo.email || t('contractCard.ownerInfoNotAvailable')
    }
    
    const formatDate = (date) => {
      if (!date) return t('contractCard.notSpecified');
      
      let dateObj;
      
      try {
        // Manejar diferentes formatos de fecha
        if (date instanceof Date) {
          dateObj = date;
        } else if (typeof date === 'object' && date.toDate) {
          // Timestamp de Firestore
          dateObj = date.toDate();
        } else if (typeof date === 'string') {
          // Cadena ISO o similar
          dateObj = new Date(date);
        } else {
          // Cualquier otro formato
          dateObj = new Date(date);
        }
        
        if (isNaN(dateObj.getTime())) {
          return t('contractCard.invalidDate');
        }
        
        return dateObj.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (error) {
        return t('contractCard.dateError');
      }
    }
    
    
    const openDeleteModal = () => {
      // Implement modal logic
    }
    
    const closeDeleteModal = () => {
      // Implement modal logic
    }
    
    const confirmDelete = () => {
      emit('delete', props.contract.id)
      closeDeleteModal()
    }

    // Función para manejar el toggle de guardado
    const handleToggleSave = () => {
      emit('toggleSave', props.contract)
    }
    
    // Funciones para el badge de compatibilidad/match
    const getMatchBadgeClass = () => {
      if (!props.contract.matchData) return ''
      const score = props.contract.matchData.score
      
      if (score >= 80) {
        // Match excelente - fondo blanco con borde dorado
        return 'shadow-lg'
      } else if (score >= 60) {
        // Buen match - fondo blanco con borde verde
        return 'shadow-lg'
      } else if (score >= 50) {
        // Match moderado - fondo blanco normal
        return 'shadow-md'
      }
      return ''
    }
    
    const getMatchIconColor = () => {
      if (!props.contract.matchData) return 'text-gray-400'
      const score = props.contract.matchData.score
      
      if (score >= 80) return 'text-amber-500'
      if (score >= 60) return 'text-green-500'
      if (score >= 50) return 'text-indigo-500'
      return 'text-gray-400'
    }
    
    const getMatchScoreText = () => {
      if (!props.contract.matchData) return ''
      const score = props.contract.matchData.score
      
      if (score >= 80) return `Ideal para ti`
      
      return 'Recomendado'
    }
    
    const getMatchLevelText = () => {
      if (!props.contract.matchData) return ''
      const score = props.contract.matchData.score
      
      if (score >= 80) return '🌟 Compatibilidad Excelente'
      if (score >= 60) return '✨ Buena Compatibilidad'
      if (score >= 50) return '👍 Compatible'
      return 'Compatible'
    }
    
    const getMatchTooltip = () => {
      return t('contractCard.recommendedByProfile')
    }
    
    // ═══ MEJORAS DE CTR NIVEL 1 ═══════════════════════════════════════════
    
    // MEJORA B: Detectar disponibilidad baja (urgencia)
    const isSpacestSearchCard = computed(() =>
      resolveExternalProvider(props.contract) === 'spacest',
    )

    const showSpacestPromoByPrice = computed(() =>
      props.isColivingSearchResult && isSpacestSearchCard.value,
    )

    const spacestPromoByPriceLabel = computed(() => {
      if (!showSpacestPromoByPrice.value) return ''
      const code = getSpacestPromoCopy()?.code || 'COOLLIVING10'
      return t('contractCard.spacestPromoByPrice', { code })
    })

    const spacestFlatRoomsLabel = computed(() => {
      if (!props.isColivingSearchResult || !isSpacestPartnerListing(props.contract)) return ''
      const grouped = Number(props.contract.spacestGroupedRoomCount)
      const rooms = Number.isFinite(grouped) && grouped > 1
        ? grouped
        : Number(props.contract.availableRooms)
      if (!Number.isFinite(rooms) || rooms <= 1) return ''
      return t('contractCard.spacestRoomsInFlat', rooms, { n: rooms })
    })

    const bluegroundBuildingUnitsLabel = computed(() => {
      if (!props.isColivingSearchResult || !isBluegroundAffiliateListing(props.contract)) return ''
      const grouped = Number(props.contract.bluegroundGroupedUnitCount)
      const units = Number.isFinite(grouped) && grouped > 1
        ? grouped
        : Number(props.contract.availableRooms)
      if (!Number.isFinite(units) || units <= 1) return ''
      return t('contractCard.bluegroundUnitsInBuilding', units, { n: units })
    })

    const affiliateGroupedInventoryLabel = computed(() =>
      spacestFlatRoomsLabel.value || bluegroundBuildingUnitsLabel.value,
    )

    const lowAvailability = computed(() => {
      if (!props.isColivingSearchResult || !props.contract.availableRooms) return false
      if (isSpacestSearchCard.value || isBluegroundAffiliateListing(props.contract)) return false
      return props.contract.availableRooms <= 2
    })
    
    // ═══ MEJORAS DE CTR NIVEL 2 ═══════════════════════════════════════════
    
    // NIVEL 2 - A: Badge de VALOR (precio competitivo)
    const showValueBadge = computed(() => {
      if (!props.isColivingSearchResult) return false
      if (!props.contract.marketComparison) return false
      
      const priceRank = props.contract.marketComparison.priceRank
      return priceRank === 'very_cheap' || priceRank === 'cheap'
    })
    
    // NIVEL 2 - B: Texto de comparativa de mercado
    const marketComparisonText = computed(() => {
      if (!props.isColivingSearchResult) return ''
      if (!props.contract.marketComparison) return ''
      
      const { priceRank, centralityRank, marketContext } = props.contract.marketComparison
      
      // Prioridad 1: Precio muy barato
      if (priceRank === 'very_cheap') {
        return `Top 10% más barato en ${props.contract.propertyLocation || 'la zona'}`
      }
      
      // Prioridad 2: Precio barato
      if (priceRank === 'cheap') {
        return `Por debajo de la media en ${props.contract.propertyLocation || 'la zona'}`
      }
      
      // Prioridad 3: Ubicación muy céntrica
      if (centralityRank === 'very_central') {
        return 'Ubicación muy céntrica'
      }
      
      // Prioridad 4: Ubicación céntrica
      if (centralityRank === 'central') {
        return 'Zona céntrica'
      }
      
      return ''
    })
    
    // NIVEL 2 - C: Carrusel de imágenes al hover
    const currentImageIndex = ref(0)
    const listingImageErrorCount = ref(0)
    let carouselRafId = 0
    let carouselLastAdvance = 0

    const validImages = computed(() => {
      if (!props.contract.images || props.contract.images.length === 0) return []
      const imagesArray = Array.isArray(props.contract.images) ? props.contract.images : []
      return imagesArray.filter((img) => img && typeof img === 'string' && img.trim() !== '')
    })

    const imageCount = computed(() => validImages.value.length)

    const firstImageUrl = computed(() => {
      const first = validImages.value[0]
      if (!first) return ''
      return processListingImageUrl(first)
    })

    const currentImageUrl = computed(() => {
      const images = validImages.value
      if (images.length === 0) return ''
      const idx = Math.min(currentImageIndex.value, images.length - 1)
      const currentImage = images[idx] || images[0]
      return processListingImageUrl(currentImage)
    })

    const displayImageUrl = computed(() => {
      const url = currentImageUrl.value
      if (!url) return ''
      if (!props.isColivingSearchResult) return url
      return getColivingListImageUrl(url, props.contract.listImageUrl || '')
    })

    watch(() => props.contract.id, () => {
      listingImageErrorCount.value = 0
      currentImageIndex.value = 0
    })

    function handleListingImageError() {
      const images = validImages.value
      if (images.length <= 1) return
      listingImageErrorCount.value += 1
      const nextIdx = listingImageErrorCount.value
      if (nextIdx < images.length) {
        currentImageIndex.value = nextIdx
      }
    }

    // Touch swipe
    let touchStartX = 0
    let touchStartY = 0

    const handleTouchStart = (e) => {
      if (!props.isColivingSearchResult) return
      touchStartX = e.touches[0].clientX
      touchStartY = e.touches[0].clientY
    }

    const handleTouchEnd = (e) => {
      if (!props.isColivingSearchResult) return
      const images = validImages.value
      if (images.length <= 1) return

      const dx = e.changedTouches[0].clientX - touchStartX
      const dy = e.changedTouches[0].clientY - touchStartY

      // Solo activar si el swipe es predominantemente horizontal (no scroll vertical)
      if (Math.abs(dx) < 30 || Math.abs(dx) < Math.abs(dy)) return

      if (dx < 0) {
        // Swipe izquierda → siguiente imagen
        currentImageIndex.value = (currentImageIndex.value + 1) % images.length
      } else {
        // Swipe derecha → imagen anterior
        currentImageIndex.value = (currentImageIndex.value - 1 + images.length) % images.length
      }
    }

    const goToImage = (idx) => {
      currentImageIndex.value = idx
    }
    
    const startImageCarousel = () => {
      if (!props.isColivingSearchResult) return
      const images = validImages.value
      if (images.length <= 1) return
      if (carouselRafId) return

      carouselLastAdvance = performance.now()
      const tick = (now) => {
        if (now - carouselLastAdvance >= CAROUSEL_ADVANCE_MS) {
          carouselLastAdvance = now
          const len = validImages.value.length
          if (len > 1) {
            currentImageIndex.value = (currentImageIndex.value + 1) % len
          }
        }
        carouselRafId = requestAnimationFrame(tick)
      }
      carouselRafId = requestAnimationFrame(tick)
    }

    const stopImageCarousel = () => {
      if (carouselRafId) {
        cancelAnimationFrame(carouselRafId)
        carouselRafId = 0
      }
      currentImageIndex.value = 0
    }

    onBeforeUnmount(() => {
      if (carouselRafId) {
        cancelAnimationFrame(carouselRafId)
        carouselRafId = 0
      }
    })

    const shouldPrioritizeImage = computed(() =>
      props.isColivingSearchResult && props.resultIndex >= 0 && props.resultIndex <= 1
    )

    
    // MEJORA B: Detectar listing nuevo
    const isNewListing = computed(() => {
      if (!props.isColivingSearchResult) return false
      
      const createdAt = props.contract.createdAt || props.contract.lastSignificantUpdate
      if (!createdAt) return false
      
      try {
        let dateObj
        if (createdAt.toDate && typeof createdAt.toDate === 'function') {
          dateObj = createdAt.toDate()
        } else if (createdAt instanceof Date) {
          dateObj = createdAt
        } else if (typeof createdAt === 'string') {
          dateObj = new Date(createdAt)
        } else {
          return false
        }
        
        const daysSinceCreated = (Date.now() - dateObj.getTime()) / (1000 * 60 * 60 * 24)
        return daysSinceCreated <= 7
      } catch {
        return false
      }
    })
    
    // MEJORA E: Optimizar título (sin redundancias)
    const optimizedTitle = computed(() => {
      const spacestTitle = resolveSpacestSearchDisplayTitle({
        propertyAddress: props.contract.propertyAddress,
        propertyLocation: props.contract.propertyLocation,
        propertyNeighborhood: props.contract.propertyNeighborhood,
        title: props.contract.title,
        source: props.contract.source || props.contract.externalSource,
        externalSource: props.contract.externalSource,
      })

      if (!props.contract.title && !spacestTitle) return ''

      let title = spacestTitle || props.contract.title
      
      // Solo optimizar para resultados de búsqueda de colivings
      if (!props.isColivingSearchResult) return title
      
      // Quitar redundancias de ciudad
      if (props.contract.propertyLocation) {
        const location = props.contract.propertyLocation.toLowerCase()
        const locationWords = location.split(/[\s,]+/)
        
        locationWords.forEach(word => {
          if (word.length > 3) {
            const regex = new RegExp(`\\b${word}\\b`, 'gi')
            const matches = (title.match(regex) || []).length
            if (matches > 1) {
              title = title.replace(regex, word)
            }
          }
        })
      }
      
      // Quitar "Coliving" del inicio si está
      title = title.replace(/^coliving\s+/i, '').trim()
      
      // Limitar longitud
      if (title.length > 60) {
        title = title.substring(0, 57) + '...'
      }
      
      return title || props.contract.title
    })
    
    // ═══ FIN MEJORAS CTR ═══════════════════════════════════════════
    
    // Computed para calcular tiempo restante de la estancia (solo si showActiveStayInfo está activo)
    const timeRemaining = computed(() => {
      if (!props.showActiveStayInfo) return null
      
      // Obtener fecha de finalización
      const endDate = props.contract.endDate || 
                      props.contract.desiredEndDate || 
                      props.contract.applicationData?.desiredEndDate ||
                      props.contract.applicationData?.calculatedEndDate
      
      if (!endDate) return null
      
      // Convertir a Date object
      let end
      try {
        if (endDate.toDate && typeof endDate.toDate === 'function') {
          end = endDate.toDate()
        } else if (endDate instanceof Date) {
          end = endDate
        } else if (typeof endDate === 'string') {
          end = new Date(endDate)
        } else {
          return null
        }
        
        // Verificar si la fecha es válida
        if (isNaN(end.getTime())) {
          return null
        }
      } catch (error) {
        console.warn('Error al convertir fecha de finalización:', error)
        return null
      }
      
      const now = new Date()
      
      // Calcular diferencia en meses
      const diffMonths = (end.getFullYear() - now.getFullYear()) * 12 + 
                         (end.getMonth() - now.getMonth())
      
      // Calcular días restantes
      const diffTime = end.getTime() - now.getTime()
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      // Determinar si mostrar alerta (menos de 30 días)
      const isUrgent = diffDays <= 30 && diffDays > 0
      
      if (diffMonths >= 1) {
        return {
          value: diffMonths,
          unit: diffMonths === 1 ? t('contractCard.month') : t('contractCard.months'),
          text: `${diffMonths} ${diffMonths === 1 ? t('contractCard.month') : t('contractCard.months')} ${t('contractCard.remaining')}`,
          endDate: end,
          daysRemaining: Math.max(0, diffDays),
          isUrgent: false
        }
      }
      
      // Si es menos de 1 mes, mostrar días
      return {
        value: Math.max(0, diffDays),
        unit: diffDays === 1 ? t('contractCard.day') : t('contractCard.days'),
        text: `${Math.max(0, diffDays)} ${diffDays === 1 ? t('contractCard.day') : t('contractCard.days')} ${t('contractCard.remaining')}`,
        endDate: end,
        daysRemaining: Math.max(0, diffDays),
        isUrgent: isUrgent
      }
    })
    
    // Función para formatear fecha completa
    const formatDateLong = (date) => {
      if (!date) return ''
      
      try {
        let dateObj
        if (date.toDate && typeof date.toDate === 'function') {
          dateObj = date.toDate()
        } else if (date instanceof Date) {
          dateObj = date
        } else if (typeof date === 'string') {
          dateObj = new Date(date)
        } else {
          return ''
        }
        
        if (isNaN(dateObj.getTime())) {
          return ''
        }
        
        return dateObj.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      } catch (error) {
        console.warn('Error al formatear fecha:', error)
        return ''
      }
    }
    
    return {
      t,
      showDeleteModal,
      showExternalModal,
      getMatchBadgeClass,
      getMatchIconColor,
      getMatchScoreText,
      getMatchLevelText,
      getMatchTooltip,
      displayPrice,
      shouldShowPrice,
      showFromLabel,
      hasIncludedServices,
      hasAllServicesIncluded,
      inclusionBadge,
      showVecyRating,
      // Funciones para badge de tipo
      getTypeBadgeClass,
      getTypeBadgeText,
      getUserRoleBadge,
      getDefaultStatusClass,
      getDefaultStatusText,
      getOwnerInfo,
      formatDate,
      firstImageUrl,
      imageCount,
      displayImageUrl,
      openDeleteModal,
      closeDeleteModal,
      confirmDelete,
      // Funciones para colivings externos
      openExternalModal,
      closeExternalModal,
      goToExternalUrl,
      capitalizeSource,
      normalizeProviderSlug,
      // Función para guardar/desguardar
      handleToggleSave,
      // Función para manejar click en toda la tarjeta
      handleCardClick,
      // Computed para el componente dinámico de enlace
      cardLinkComponent,
      cardHref,
      cardTo,
      hasDirectBooking,
      // Información de estancia activa
      timeRemaining,
      formatDateLong,
      // Retirar solicitud
      isWithdrawing,
      isWithdrawableColiving,
      handleWithdraw,
      // Mejoras CTR Nivel 1
      affiliateGroupedInventoryLabel,
      showSpacestPromoByPrice,
      spacestPromoByPriceLabel,
      lowAvailability,
      isNewListing,
      optimizedTitle,
      // Mejoras CTR Nivel 2
      showValueBadge,
      marketComparisonText,
      currentImageIndex,
      startImageCarousel,
      stopImageCarousel,
      handleTouchStart,
      handleTouchEnd,
      goToImage,
      listingImageErrorCount,
      handleListingImageError,
      shouldPrioritizeImage,
      showQuickPreview,
      handleQuickPreviewCTA,
      // Funciones auxiliares para modal Quick Preview
      getMarketComparisonTitle,
      getCentralityTitle,
      getServicesTitle,
      getFlexibilityTitle,
      getHighlightLabel,
      currencySymbol,
      // Compartir
      shareCopied,
      handleShare,
    }
  }
}
</script>

<style scoped>
/* Quitar estilos de enlace cuando se usa <a> como wrapper */
a.no-underline {
  text-decoration: none;
  color: inherit;
}

a.no-underline:hover {
  text-decoration: none;
  color: inherit;
}

/* Add any component-specific styles here */
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Animación sutil de pulso para el badge de match */
@keyframes pulse-subtle {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.95;
    transform: scale(1.02);
  }
}

.animate-pulse-subtle {
  animation: pulse-subtle 2s ease-in-out infinite;
}

/* Animación de rebote lento para el icono de estrella */
@keyframes bounce-slow {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-2px);
  }
}

.animate-bounce-slow {
  animation: bounce-slow 1.5s ease-in-out infinite;
}

/* NIVEL 2: Animación para modal Quick Preview */
@keyframes modal-in {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.animate-modal-in {
  animation: modal-in 0.2s ease-out forwards;
}
</style>
